import React, { createContext, useContext } from 'react';
import useGameAudio from '../hooks/useGameAudio';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  // Use the existing audio hook
  const {
    audioEnabled,
    volume,
    enableAudio,
    handleVolumeChange,
    playRandomBombSound,
    playRandomBombDropSound,
    playRandomBombKickSound,
    playRandomBombStopSound,
    playRandomBombBounceSound,
    playRandomUpgradePickupSound,
    playSuddenDeathStartSound,
    playRandomSuddenDeathBlockSound,
    handleSoundtrackUpdate,
    stopSoundtrack,
    currentSoundtrack,
    playPoopSound
  } = useGameAudio();

  // Audio actions
  const actions = React.useMemo(() => ({
    enableAudio,
    handleVolumeChange,
    playRandomBombSound,
    playRandomBombDropSound,
    playRandomBombKickSound,
    playRandomBombStopSound,
    playRandomBombBounceSound,
    playRandomUpgradePickupSound,
    playSuddenDeathStartSound,
    playRandomSuddenDeathBlockSound,
    handleSoundtrackUpdate,
    stopSoundtrack,
    playPoopSound,
    
    // Audio utilities
    toggleAudio: () => enableAudio(!audioEnabled),
    setVolume: handleVolumeChange,
    
    // Sound effect shortcuts
    playBombDrop: playRandomBombDropSound,
    playBombExplode: playRandomBombSound,
    playBombKick: playRandomBombKickSound,
    playBombStop: playRandomBombStopSound,
    playBombBounce: playRandomBombBounceSound,
    playUpgradePickup: playRandomUpgradePickupSound,
    playSuddenDeathStart: playSuddenDeathStartSound,
    playSuddenDeathBlock: playRandomSuddenDeathBlockSound
  }), [
    audioEnabled,
    enableAudio,
    handleVolumeChange,
    playRandomBombSound,
    playRandomBombDropSound,
    playRandomBombKickSound,
    playRandomBombStopSound,
    playRandomBombBounceSound,
    playRandomUpgradePickupSound,
    playSuddenDeathStartSound,
    playRandomSuddenDeathBlockSound,
    handleSoundtrackUpdate,
    stopSoundtrack,
    playPoopSound
  ]);

  // Memoized state values
  const state = React.useMemo(() => ({
    audioEnabled,
    volume,
    currentSoundtrack
  }), [audioEnabled, volume, currentSoundtrack]);

  const value = React.useMemo(() => ({
    ...state,
    ...actions
  }), [state, actions]);

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

export default AudioContext;